import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getProducts, getProduct, updateProduct, getProductCategories } from '@/services/products.api';
import { getErrorMessage } from '@/services/api';
import type { UpdateProductRequest } from '@wooconnect/shared';
import { showSuccess, showError } from '@/utils/toast';

export function useProducts(siteId: string | null, filters: { page?: number; perPage?: number; search?: string; status?: string; stockStatus?: string; category?: number }) {
  return useQuery({
    queryKey: ['products', siteId, filters],
    queryFn: () => getProducts(siteId!, filters),
    enabled: !!siteId,
  });
}

export function useProductDetail(siteId: string | null, productId: number) {
  return useQuery({
    queryKey: ['product', siteId, productId],
    queryFn: () => getProduct(siteId!, productId),
    enabled: !!siteId && !!productId,
  });
}

export function useProductCategories(siteId: string | null) {
  return useQuery({
    queryKey: ['productCategories', siteId],
    queryFn: () => getProductCategories(siteId!),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateProduct(siteId: string) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, data }: { productId: number; data: UpdateProductRequest }) =>
      updateProduct(siteId, productId, data),
    onSuccess: (_, { productId }) => {
      qc.invalidateQueries({ queryKey: ['products', siteId] });
      qc.invalidateQueries({ queryKey: ['product', siteId, productId] });
      showSuccess(t('common.success'));
    },
    onError: (err) => showError(getErrorMessage(err)),
  });
}
